import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { 
  Users, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  ArrowRight, 
  Mail, 
  MapPin,
  Zap,
  Target,
  BarChart3
} from "lucide-react";
import { mockData } from "../data/mock";

const HomePage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: ""
  });

  // Animated text state
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  
  const rotatingWords = ['Sales', 'HR', 'Admin', 'Marketing', 'Operations', 'Customer Service'];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      
      setTimeout(() => {
        setCurrentWordIndex((prevIndex) => 
          prevIndex === rotatingWords.length - 1 ? 0 : prevIndex + 1
        );
        setIsVisible(true);
      }, 300); // Half second for fade out, then fade in
      
    }, 2500); // Change every 2.5 seconds

    return () => clearInterval(interval);
  }, [rotatingWords.length]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Show immediate success message to user
    alert("Thank you for your interest! We'll contact you within 24 hours to schedule your free consultation.");
    
    // Google Apps Script Web App URL for Google Sheets integration
    const scriptURL = 'https://script.google.com/macros/s/AKfycbz4Li_7LJMxonchEjdbEgpnzazAiFSDjft3EPHdhp7qs9ZRP4wwTN_UOw3i6nt12k10/exec';
    
    // Submit to Google Sheets in background
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);
      
      // Use no-cors mode to avoid CORS issues
      fetch(scriptURL, {
        method: 'POST',
        mode: 'no-cors',
        body: formDataToSend
      });
      
    } catch (error) {
      console.log('Background submission error (this is expected with no-cors mode):', error);
    }
    
    // Clear form immediately
    setFormData({
      name: "",
      email: "",
      company: "",
      phone: "",
      message: ""
    });
  };

  return (
    <div style={{ background: "var(--bg-page)", minHeight: "100vh" }}>
      {/* Header */}
      <header className="header-nav">
        <div className="container">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <div className="heading-2 logo">
              MAIN STREET SYSTEMS
            </div>
            <div className="nav-actions">
              <Button 
                variant="ghost" 
                className="btn-secondary"
                onClick={() => document.getElementById('teams-section').scrollIntoView({ behavior: 'smooth' })}
              >
                Learn More
              </Button>
              <Button variant="ghost" className="btn-secondary" onClick={() => window.location.hash = '#/case-studies'}>
                Case Studies
              </Button>
              <Button 
                className="btn-primary"
                onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
              >
                Book Consultation
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-announcement">
            <Zap size={16} />
            <span>Transform Your Business Operations</span>
          </div>
          <h1 className="heading-hero" style={{ marginBottom: "1rem" }}>
            AI Automation for Main Street Businesses
          </h1>
          <p className="body-large" style={{ marginBottom: "2rem", maxWidth: "600px", marginLeft: "auto", marginRight: "auto" }}>
            Stop losing 20-30 hours per week on manual, repetitive tasks. We help small businesses implement intelligent automation solutions that eliminate busywork and accelerate growth.
          </p>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
            <Button 
              className="btn-primary" 
              style={{ minWidth: "200px" }}
              onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
            >
              Schedule Free Consultation
            </Button>
            <Button 
              className="btn-secondary" 
              style={{ minWidth: "150px" }}
              onClick={() => document.getElementById('how-automation-helps').scrollIntoView({ behavior: 'smooth' })}
            >
              Learn How It Works
            </Button>
          </div>
        </div>
      </section>

      {/* Why Automation Matters */}
      <section id="how-automation-helps" className="pad-2xl" style={{ background: "var(--bg-section)" }}>
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <h2 className="heading-1" style={{ marginBottom: "1.5rem" }}>
              Why Automation Matters
            </h2>
            <div style={{ maxWidth: "900px", margin: "0 auto", textAlign: "left" }}>
              <p className="body-large" style={{ marginBottom: "1.5rem" }}>
                We've spent more than a decade operating, advising, and growing small to mid-sized businesses, so we know firsthand the pain points that drain productivity and stall growth.
              </p>
              <p className="body-large" style={{ 
                marginBottom: "1.5rem", 
                fontWeight: "600", 
                color: "var(--accent-primary)",
                textAlign: "center",
                fontSize: "1.25rem"
              }}>
                On average, small businesses waste 20–30 hours each week on manual, repetitive tasks.
              </p>
              <p className="body-large" style={{ marginBottom: "1.5rem" }}>
                Our role is simple: audit your workflows and design intelligent automations that eliminate busywork, reduce errors, and accelerate processes to give you lower costs, higher productivity, and a lasting competitive edge.
              </p>
              <p className="body-large" style={{ 
                fontWeight: "500", 
                color: "var(--text-primary)",
                textAlign: "center"
              }}>
                With over 500 workflows engineered for clients, we're confident your business has untapped opportunities for automation today.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Teams Benefiting */}
      <section id="teams-section" className="pad-2xl">
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <div className="animated-subheadline" style={{ marginBottom: "1.5rem" }}>
              <span className="static-text">AI Systems For </span>
              <span 
                className={`rotating-text ${isVisible ? 'visible' : 'hidden'}`}
                style={{ color: "var(--accent-primary)" }}
              >
                {rotatingWords[currentWordIndex]}
              </span>
            </div>
            <h2 className="heading-1" style={{ marginBottom: "1rem" }}>
              Teams Benefiting From Our Automation
            </h2>
          </div>
          
          <Tabs defaultValue="sales" className="w-full">
            <TabsList className="premium-tabs-list">
              <TabsTrigger value="sales" className="button-text premium-tab-trigger">Sales</TabsTrigger>
              <TabsTrigger value="hr" className="button-text premium-tab-trigger">HR & Admin</TabsTrigger>
              <TabsTrigger value="operations" className="button-text premium-tab-trigger">Operations</TabsTrigger>
              <TabsTrigger value="marketing" className="button-text premium-tab-trigger">Marketing</TabsTrigger>
              <TabsTrigger value="customer" className="button-text premium-tab-trigger">Customer Service</TabsTrigger>
              <TabsTrigger value="more" className="button-text premium-tab-trigger">More</TabsTrigger>
            </TabsList>
            
            {mockData.teams.map((team) => (
              <TabsContent key={team.id} value={team.id}>
                <div className="voice-grid">
                  {team.automations.map((automation, index) => (
                    <Card key={index} className={`voice-card ${automation.accentColor}`} style={{ padding: "1.2rem" }}>
                      <div style={{ marginBottom: "1rem" }}>
                        <h3 className="heading-3" style={{ marginBottom: "0.5rem" }}>{automation.title}</h3>
                        <p className="body-small">{automation.description}</p>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginTop: "1.5rem" }}>
                        <div>
                          <h4 className="body-small" style={{ fontWeight: "500", marginBottom: "0.5rem", color: "var(--text-muted)" }}>Before:</h4>
                          <p className="caption">{automation.before}</p>
                        </div>
                        <div>
                          <h4 className="body-small" style={{ fontWeight: "500", marginBottom: "0.5rem", color: "var(--text-muted)" }}>After:</h4>
                          <p className="caption" style={{ color: "var(--accent-primary)" }}>{automation.after}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Results Section */}
      <section className="pad-2xl" style={{ background: "var(--bg-section)" }}>
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <h2 className="heading-1" style={{ marginBottom: "1rem" }}>
              Typical Results
            </h2>
            <p className="body-large">
              See the measurable impact our automation solutions deliver
            </p>
          </div>
          
          <div className="results-grid">
            {mockData.results.map((result, index) => (
              <Card key={index} className="metrics-card">
                <div style={{ 
                  fontSize: "3rem", 
                  fontWeight: "700", 
                  marginBottom: "1rem", 
                  color: "var(--accent-primary)",
                  background: `linear-gradient(135deg, var(--accent-primary) 0%, #FFFFFF 100%)`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent"
                }}>
                  {result.metric}
                </div>
                <h3 className="heading-3" style={{ marginBottom: "0.75rem" }}>{result.title}</h3>
                <p className="body-small">{result.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact-form" className="pad-2xl" style={{ background: "var(--gradient-section)" }}>
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <h2 className="heading-1" style={{ marginBottom: "1.5rem" }}>
              Ready to Transform Your Business Operations?
            </h2>
            <p className="body-large" style={{ maxWidth: "900px", margin: "0 auto" }}>
              Want to learn more? Schedule a free strategic consultation with our founder. We'll audit your current workflows and create a custom automation roadmap showing exactly how to reclaim valuable time every week while reducing costs and accelerating growth.
            </p>
          </div>
          
          <div style={{ maxWidth: "700px", margin: "0 auto" }}>
            <Card className="voice-card" style={{ padding: "2.5rem" }}>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ marginBottom: "2rem" }}>
                  <div>
                    <Label htmlFor="name" className="body-small" style={{ color: "var(--text-primary)" }}>Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="premium-input"
                      style={{ marginTop: "0.5rem" }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="body-small" style={{ color: "var(--text-primary)" }}>Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="premium-input"
                      style={{ marginTop: "0.5rem" }}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ marginBottom: "2rem" }}>
                  <div>
                    <Label htmlFor="company" className="body-small" style={{ color: "var(--text-primary)" }}>Company</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="premium-input"
                      style={{ marginTop: "0.5rem" }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="body-small" style={{ color: "var(--text-primary)" }}>Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="premium-input"
                      style={{ marginTop: "0.5rem" }}
                    />
                  </div>
                </div>
                
                <div style={{ marginBottom: "2.5rem" }}>
                  <Label htmlFor="message" className="body-small" style={{ color: "var(--text-primary)" }}>Tell us about your biggest operational challenges</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="premium-input"
                    style={{ marginTop: "0.5rem" }}
                  />
                </div>
                
                <Button type="submit" className="btn-primary" style={{ width: "100%" }}>
                  Schedule Free Strategic Consultation
                  <ArrowRight size={16} style={{ marginLeft: "0.75rem" }} />
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="pad-lg" style={{ background: "#000000", color: "var(--text-primary)" }}>
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="heading-2" style={{ color: "var(--text-primary)", marginBottom: "1rem" }}>
                MAIN STREET SYSTEMS
              </div>
              <p className="body-small" style={{ color: "var(--text-secondary)" }}>
                Transforming Main Street businesses through intelligent automation solutions.
              </p>
            </div>
            
            <div>
              <h3 className="heading-3" style={{ color: "var(--text-primary)", marginBottom: "1rem" }}>Contact</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <Mail size={16} style={{ color: "var(--accent-primary)" }} />
                  <span className="body-small" style={{ color: "var(--text-secondary)" }}>hello@mainstreetsystems.ai</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <MapPin size={16} style={{ color: "var(--accent-primary)" }} />
                  <span className="body-small" style={{ color: "var(--text-secondary)" }}>New York City</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="heading-3" style={{ color: "var(--text-primary)", marginBottom: "1rem" }}>Services</h3>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                <li className="body-small" style={{ color: "var(--text-secondary)", marginBottom: "0.75rem" }}>Business Process Automation</li>
                <li className="body-small" style={{ color: "var(--text-secondary)", marginBottom: "0.75rem" }}>Workflow Optimization</li>
                <li className="body-small" style={{ color: "var(--text-secondary)", marginBottom: "0.75rem" }}>Custom AI Solutions</li>
                <li className="body-small" style={{ color: "var(--text-secondary)" }}>Strategic Consulting</li>
              </ul>
            </div>
          </div>
          
          <div style={{ 
            borderTop: "1px solid var(--border-primary)", 
            paddingTop: "2rem", 
            marginTop: "3rem", 
            textAlign: "center" 
          }}>
            <p className="caption" style={{ color: "var(--text-muted)" }}>
              © 2025 Main Street Systems AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
